import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Info, MapPin, Images, ListOrdered, Wand2, Crop as CropIcon, ClipboardCheck, Play, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { prototypeApi, apiErrorMessage } from "@/lib/prototypeApi";
import { getPhoto, putPhoto, deletePhoto } from "@/lib/idb";
import { classifyPresets, presetLabel } from "@/lib/presetClassify";
import { AddressAutocomplete } from "@/components/prototype/AddressAutocomplete";
import { ImportSources } from "@/components/prototype/ImportSources";
import { PhotoGrid } from "@/components/prototype/PhotoGrid";
import { PhotoEditor } from "@/components/prototype/PhotoEditor";

const MAX = 20;
const STEPS = [
  { label: "Woninggegevens", icon: MapPin },
  { label: "Foto's importeren", icon: Images },
  { label: "Volgorde bepalen", icon: ListOrdered },
  { label: "Bewegingen en effecten", icon: Wand2 },
  { label: "Uitsnedes controleren", icon: CropIcon },
  { label: "Overzicht", icon: ClipboardCheck },
  { label: "Genereren", icon: Play },
];

const toMeta = (p) => ({ imageId: p.imageId, filename: p.filename, source: p.source, type: p.type, size: p.size, presetKey: p.presetKey || null, crop: p.crop || null });

export function Wizard({ localProjectId, config, presets, onExit, isNew = false }) {
  const classified = classifyPresets(presets);
  const [address, setAddress] = useState(null);
  const [orientation, setOrientation] = useState("");
  const [photos, setPhotos] = useState([]);
  const [step, setStep] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editor, setEditor] = useState({ open: false, index: 0, tab: "effects" });
  const [loaded, setLoaded] = useState(false);
  const replaceRef = useRef(null);
  const replaceTarget = useRef(null);
  const photosRef = useRef([]);
  useEffect(() => { photosRef.current = photos; }, [photos]);

  // ---- hydrate draft ----
  useEffect(() => {
    if (isNew) { setLoaded(true); return; }
    (async () => {
      try {
        const { data } = await prototypeApi.getProject(localProjectId);
        const d = data.draft || {};
        if (d.address) setAddress(d.address);
        if (d.orientation) setOrientation(d.orientation);
        if (typeof d.step === "number") setStep(d.step);
        const meta = d.photosMeta || [];
        const hydrated = [];
        for (const m of meta) {
          const blob = await getPhoto(localProjectId, m.imageId);
          if (blob) hydrated.push({ ...m, blob, previewUrl: URL.createObjectURL(blob) });
        }
        setPhotos(hydrated);
      } catch { /* new project */ }
      setLoaded(true);
    })();
    return () => photosRef.current.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
  }, [localProjectId, isNew]);

  // ---- autosave (debounced) ----
  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => {
      prototypeApi.saveProject({
        localProjectId,
        ratio: orientation,
        photoOrder: photos.map((p) => p.imageId),
        presetKeys: Object.fromEntries(photos.map((p) => [p.imageId, p.presetKey || "DEFAULT"])),
        draft: { address, orientation, step, photosMeta: photos.map(toMeta) },
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [address, orientation, photos, step, loaded, localProjectId]);

  // ---- photo handlers ----
  const addPhotos = useCallback((items, rejected) => {
    (rejected || []).forEach((r) => toast.error(r));
    if (!items.length) return;
    const created = items.map((it) => {
      const imageId = crypto.randomUUID();
      putPhoto(localProjectId, imageId, it.file).catch(() => {});
      return { imageId, filename: it.filename, source: it.source, type: it.type, size: it.size, presetKey: null, crop: null, blob: it.file, previewUrl: URL.createObjectURL(it.file) };
    });
    setPhotos((prev) => [...prev, ...created]);
  }, [localProjectId]);

  const removePhoto = (id) => {
    setPhotos((prev) => {
      const target = prev.find((p) => p.imageId === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      deletePhoto(localProjectId, id).catch(() => {});
      return prev.filter((p) => p.imageId !== id);
    });
    setSelectedIds((s) => s.filter((x) => x !== id));
  };

  const movePhoto = (from, to) => {
    if (to < 0 || to >= photos.length) return;
    const next = [...photos];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    setPhotos(next);
  };

  const applyPreset = (val, scope, currentId) => {
    setPhotos((prev) => prev.map((p) => {
      if (scope === "all") return { ...p, presetKey: val };
      if (scope === "selected") return selectedIds.includes(p.imageId) ? { ...p, presetKey: val } : p;
      return p.imageId === currentId ? { ...p, presetKey: val } : p;
    }));
    if (scope === "all") toast.success("Toegepast op alle foto's.");
    if (scope === "selected") toast.success(`Toegepast op ${selectedIds.length} foto's.`);
  };

  const setCrop = (id, crop) => setPhotos((prev) => prev.map((p) => (p.imageId === id ? { ...p, crop } : p)));
  const toggleSelect = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const triggerReplace = (id) => { replaceTarget.current = id; replaceRef.current?.click(); };
  const onReplaceFile = (e) => {
    const file = e.target.files?.[0];
    const id = replaceTarget.current;
    if (file && id) {
      putPhoto(localProjectId, id, file).catch(() => {});
      setPhotos((prev) => prev.map((p) => {
        if (p.imageId !== id) return p;
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
        return { ...p, filename: file.name, type: file.type, size: file.size, crop: null, blob: file, previewUrl: URL.createObjectURL(file) };
      }));
    }
    if (replaceRef.current) replaceRef.current.value = "";
  };

  // ---- navigation ----
  const canGoTo = (n) => {
    if (n >= 1 && !address) return false;
    if (n >= 2 && photos.length < 1) return false;
    return true;
  };
  const next = () => canGoTo(step + 1) && setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const cropCounts = photos.reduce((a, p) => { a[p.crop ? "custom" : "auto"]++; return a; }, { auto: 0, custom: 0 });

  return (
    <div data-testid="video-wizard">
      {/* stepper */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex min-w-max items-center gap-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            const reachable = canGoTo(i);
            return (
              <button key={i} type="button" disabled={!reachable} onClick={() => reachable && setStep(i)} data-testid={`step-nav-${i}`}
                className={`flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-medium transition-colors ${active ? "border-re-red bg-re-red text-white" : done ? "border-re-red/30 bg-re-red/5 text-re-red" : "border-re-line bg-white text-re-muted"} disabled:opacity-40`}>
                <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${active ? "bg-white/20" : done ? "bg-re-red/10" : "bg-re-soft"}`}>{done ? <Check className="h-3 w-3" /> : i + 1}</span>
                <span className="hidden sm:inline">{s.label}</span>
                <Icon className="h-3.5 w-3.5 sm:hidden" />
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-3xl border border-re-line bg-white p-6 md:p-8">
        <h2 className="mb-1 font-heading text-2xl font-bold text-re-charcoal">{STEPS[step].label}</h2>

        {/* STEP 0 — address + orientation */}
        {step === 0 && (
          <div className="mt-5 space-y-6">
            <AddressAutocomplete value={address} onChange={setAddress} config={config} />
            <div className="space-y-2">
              <p className="text-sm font-medium text-re-charcoal">Oriëntatie van de video&apos;s</p>
              <div className="flex flex-col gap-3 sm:flex-row">
                {[{ k: "portrait", t: "Portret (9:16)", d: "Voor Reels, Stories en TikTok" }, { k: "landscape", t: "Liggend (16:9)", d: "Voor websites en presentaties" }].map((r) => (
                  <button key={r.k} type="button" onClick={() => setOrientation(r.k)} data-testid={`orientation-${r.k}`}
                    className={`flex-1 rounded-2xl border p-4 text-left transition-colors ${orientation === r.k ? "border-re-red bg-re-red/5" : "border-re-line hover:border-re-red/30"}`}>
                    <p className="font-heading text-sm font-semibold text-re-charcoal">{r.t}</p>
                    <p className="text-xs text-re-muted">{r.d}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* STEP 1 — import */}
        {step === 1 && (
          <div className="mt-5 space-y-5">
            <ImportSources config={config} onAddFiles={addPhotos} currentCount={photos.length} maxPhotos={MAX} />
            {photos.length > 0 && (
              <p className="text-sm font-medium text-re-charcoal" data-testid="import-count">{photos.length} / {MAX} foto&apos;s geselecteerd</p>
            )}
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {photos.map((p, i) => (
                <div key={p.imageId} className="relative overflow-hidden rounded-lg border border-re-line">
                  <img src={p.previewUrl} alt={`Foto ${i + 1}`} className="aspect-square w-full object-cover" />
                  <button type="button" onClick={() => removePhoto(p.imageId)} data-testid={`import-remove-${i}`} className="absolute right-1 top-1 rounded-full bg-black/60 px-1.5 text-xs text-white">×</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2 — order */}
        {step === 2 && (
          <div className="mt-5 space-y-4">
            <p className="text-sm text-re-muted" data-testid="order-count">{photos.length} / {MAX} foto&apos;s geselecteerd · sleep om de volgorde te bepalen.</p>
            <PhotoGrid photos={photos} presets={presets} variant="order" onRemove={removePhoto} onMove={movePhoto} onReorder={setPhotos} onReplace={triggerReplace} />
          </div>
        )}

        {/* STEP 3 — movements & effects */}
        {step === 3 && (
          <div className="mt-5 space-y-4">
            <div className="flex items-start gap-2 rounded-xl bg-re-soft px-3 py-2 text-xs text-re-muted">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-re-red" />
              Klik op een foto om de camerabeweging of het effect te kiezen. Standaard: automatische camerabeweging.
            </div>
            <PhotoGrid photos={photos} presets={presets} variant="effects" selectedIds={selectedIds} onOpenEditor={(i, tab) => setEditor({ open: true, index: i, tab })} onRemove={removePhoto} onToggleSelect={toggleSelect} />
          </div>
        )}

        {/* STEP 4 — crop */}
        {step === 4 && (
          <div className="mt-5 space-y-4">
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="rounded-full bg-re-soft px-3 py-1 text-re-muted" data-testid="crop-auto-count">Automatisch: {cropCounts.auto}</span>
              <span className="rounded-full bg-re-red/10 px-3 py-1 text-re-red" data-testid="crop-custom-count">Aangepast: {cropCounts.custom}</span>
            </div>
            <PhotoGrid photos={photos} presets={presets} variant="crop" selectedIds={selectedIds} onOpenEditor={(i, tab) => setEditor({ open: true, index: i, tab })} onRemove={removePhoto} onToggleSelect={toggleSelect} />
          </div>
        )}

        {/* STEP 5 — review */}
        {step === 5 && (
          <div className="mt-5 space-y-5" data-testid="review">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-re-line p-4">
                <p className="text-xs text-re-muted">Adres</p>
                <p className="text-sm font-medium text-re-charcoal">{address?.formattedAddress || "—"}</p>
              </div>
              <div className="rounded-xl border border-re-line p-4">
                <p className="text-xs text-re-muted">Oriëntatie</p>
                <p className="text-sm font-medium text-re-charcoal">{orientation === "portrait" ? "Portret (9:16)" : orientation === "landscape" ? "Liggend (16:9)" : "—"}</p>
              </div>
              <div className="rounded-xl border border-re-line p-4">
                <p className="text-xs text-re-muted">Aantal foto&apos;s</p>
                <p className="text-sm font-medium text-re-charcoal">{photos.length}</p>
              </div>
              <div className="rounded-xl border border-re-line p-4">
                <p className="text-xs text-re-muted">Uitsnedes</p>
                <p className="text-sm font-medium text-re-charcoal">{cropCounts.auto} automatisch · {cropCounts.custom} aangepast</p>
              </div>
            </div>
            <div className="rounded-xl border border-re-line p-4">
              <p className="mb-2 text-xs text-re-muted">Volgorde, beweging/effect &amp; uitsnede per foto</p>
              <ol className="space-y-1.5">
                {photos.map((p, i) => (
                  <li key={p.imageId} className="flex items-center gap-3 text-sm">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-re-soft text-xs font-bold text-re-charcoal">{i + 1}</span>
                    <img src={p.previewUrl} alt="" className="h-8 w-8 rounded object-cover" />
                    <span className="min-w-0 flex-1 truncate text-re-charcoal">{p.filename}</span>
                    <span className="text-xs text-re-muted">{presetLabel(p.presetKey, presets)}</span>
                    <span className="text-xs text-re-muted">· {p.crop ? "aangepast" : "auto"}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              Portret en liggend zijn aparte projecten en verbruiken elk een eigen woningcredit.
            </div>
          </div>
        )}

        {/* STEP 6 — generate (disabled) */}
        {step === 6 && (
          <div className="mt-5 space-y-5" data-testid="generate-step">
            <p className="text-sm text-re-muted">Alles staat klaar. Genereren maakt van je foto&apos;s professionele woningvideo&apos;s.</p>
            <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="owner-generate-notice">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" />
              <span><b>Owner-melding:</b> Generatie tijdelijk geblokkeerd tijdens de interfacecontrole.</span>
            </div>
            <Button disabled data-testid="generate-button" className="w-full rounded-full bg-re-red py-6 text-base font-semibold text-white opacity-60">
              <Play className="mr-2 h-5 w-5" /> Video&apos;s genereren
            </Button>
          </div>
        )}

        {/* nav buttons */}
        <div className="mt-8 flex items-center justify-between border-t border-re-line pt-5">
          <Button variant="outline" onClick={step === 0 ? onExit : back} data-testid="wizard-back" className="rounded-full">
            <ArrowLeft className="mr-1.5 h-4 w-4" /> {step === 0 ? "Naar projecten" : "Vorige"}
          </Button>
          {step < STEPS.length - 1 && (
            <Button onClick={next} disabled={!canGoTo(step + 1)} data-testid="wizard-next" className="rounded-full bg-re-charcoal text-white hover:bg-re-ink disabled:opacity-40">
              Volgende <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <PhotoEditor
        open={editor.open}
        photos={photos}
        index={editor.index}
        classified={classified}
        orientation={orientation || "portrait"}
        selectedIds={selectedIds}
        initialTab={editor.tab}
        onClose={() => setEditor((e) => ({ ...e, open: false }))}
        onNavigate={(i) => setEditor((e) => ({ ...e, index: i }))}
        onApplyPreset={applyPreset}
        onSetCrop={setCrop}
      />
      <input ref={replaceRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onReplaceFile} data-testid="replace-input" />
    </div>
  );
}
